import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IOrganizationalIds,
  IOrganizationalUnit,
  getAllOrganizationalUnits,
  getClientContactsCount,
  getClientsCount,
  getSelectedOUClients,
  selectedOrganizationalUnitClientManagers,
} from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  EmptyTeamsAndUsersIcon,
  WsCard,
  CardVariant,
  WsColors,
  WsTypoColors,
  getDateUsingFormat,
} from 'wisestep-base';

import OrgUnit from '../../../images/Avatar/OrgUnit.svg';
import Loader from '../../conversationWidget/components/Loader';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import SearchBar from '../../teams/components/SearchBarWrapper';
import { StyledDataGrid } from '../../teams/components/TeamListNew';
import WsAvatar from '../../teams/components/WsAvatar';
import { ScopeType } from '../helper/ClientSettingsHelper';

import { StyledPaginationBox } from './OrganizationalUnitLayoutNew';
import { countsMap } from './OrganizationalUnitSettings';

interface IClientUnits {
  title: string;
  scopeType: string;
}

interface ITableState {
  organizationalUnitName: string;
  index: number;
  id: number | string;
}

const ClientUnits = ({ title, scopeType }: IClientUnits) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const methods = useForm();
  const { setValue, handleSubmit } = methods;
  const [organizationalUnits, setOrganizationalUnits] = useState<IOrganizationalUnit[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [clientCount, setClientCount] = useState<countsMap>({});
  const isClientsOrganizationalUnit: boolean =
    path == '/workspace-users-and-teams/client-units' ? true : false;
  const isClientContactOrganizationalUnit: boolean =
    path == '/workspace-users-and-teams/client-contacts-units' ? true : false;
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [tableData, setTableData] = useState<ITableState[]>([]);
  const fetchData = useCallback(async () => {
    setLoader(true);
    try {
      const response = await getAllOrganizationalUnits(scopeType);
      setOrganizationalUnits(response?.data);
      const orgIds = response?.data.map((res: { id: any }) => res.id ?? '');
      setLoader(false);
      const payload: IOrganizationalIds = {
        organizationalIds: orgIds,
      };
      const res =
        scopeType === 'Clients'
          ? await getClientsCount(payload)
          : await getClientContactsCount(payload);
      setClientCount(res?.data?.countsByOrganizationalId);
    } catch (error) {
      setOrganizationalUnits([]);
    } finally {
      setLoader(false);
    }
  }, [scopeType]);

  const getData = useCallback(() => {
    return (
      organizationalUnits?.map((orgUnit, index) => ({
        id: orgUnit.id ?? index,
        createdOn: orgUnit.createdOn
          ? getDateUsingFormat(orgUnit.createdOn.toString(), 'DD MMM YYYY')
          : '',
        organizationalUnitName: orgUnit.name ?? '',
        index: index,
      })) || []
    );
  }, [organizationalUnits]);

  useEffect(() => {
    setOrganizationalUnits([]);
    setClientCount({});
    fetchData();
  }, [fetchData, scopeType]);

  useEffect(() => {
    setTableData(getData());
  }, [getData, organizationalUnits]);

  const onDrillDown = async (index: number) => {
    const clientOrClientContactDataList =
      scopeType === 'Clients'
        ? await getSelectedOUClients(organizationalUnits?.[index].id ?? '')
        : await selectedOrganizationalUnitClientManagers(organizationalUnits?.[index].id ?? '');
    const ids = clientOrClientContactDataList?.data?.map((item) =>
      scopeType === 'Clients' ? item?.clientId : item?.clientManagerId
    );
    const url = '/organizational_unit/user-view';
    const state = {
      organizationalUnitDescription: organizationalUnits?.[index].description,
      organizationalUnitName: organizationalUnits?.[index].name,
      organizationalUnitId: organizationalUnits?.[index].id,
      organizationalUnitScopeType: organizationalUnits?.[index].scopeType,
      isSettingsPage: false,
      isClientsOrganizationalUnit: isClientsOrganizationalUnit,
      isClientContactOrganizationalUnit: isClientContactOrganizationalUnit,
      clientOrClientContactIds: ids,
    };
    navigate(url, {
      state: state,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'organizationalUnitName',
      headerName:
        scopeType == ScopeType.Clients
          ? 'Client Unit Name'
          : scopeType == ScopeType.Client_Contacts
          ? 'Client Contact Unit Name'
          : 'Organizational Unit Name',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Grid spacing={2} alignItems="center" container>
          <Grid item>
            <WsAvatar src={OrgUnit} variant="rounded" />
          </Grid>
          <Grid item>
            <WsTypography
              cursor="pointer"
              color={WsTypoColors.PRIMARY750}
              variant={WsTypoVariants.SUB_TITLE}
              onClick={() => onDrillDown(params.row.index)}
            >
              {params.value}
            </WsTypography>
          </Grid>
        </Grid>
      ),
    },
    {
      field: 'id',
      headerName:
        scopeType == ScopeType.Clients
          ? 'Clients'
          : scopeType == ScopeType.Client_Contacts
          ? 'Client Contacts'
          : 'Count',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Grid container direction="row" sx={{ cursor: 'pointer' }} spacing={1}>
          <Grid item>
            <WsTypography variant={WsTypoVariants.BODY1}>
              {clientCount?.[params.value] ?? '0'}
            </WsTypography>
          </Grid>
        </Grid>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Date Created',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <WsTypography variant={WsTypoVariants.BODY1}>{params.value}</WsTypography>
      ),
    },
  ];

  const resetTofetch = (returnFlag) => {
    const data = getData();
    setTableData(data);
    if (returnFlag) return data;
  };

  const onSearch = (data) => {
    const originalData = resetTofetch(true);
    if (data.query) {
      const filteredData = originalData?.filter((item) =>
        item.organizationalUnitName.toLowerCase().includes(data.query.toLowerCase())
      );
      filteredData && setTableData(filteredData);
      setPageNo(0);
    } else {
      clear();
    }
  };

  const clear = () => {
    setValue('query', '');
    resetTofetch(false);
  };

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {tableData.length}
        </WsTypography>
      </Box>
      {tableData.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(tableData.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  return (
    <Grid
      container
      bgcolor={WsColors.White}
      sx={{ boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)', borderRadius: 2, p: 2 }}
      overflow="hidden"
    >
      <Grid item xs={8} ml={2} display="flex" gap={1.5} alignItems="center">
        <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
          {title}
        </WsTypography>
        <Tooltip title={`View ${title} details here`}>
          <IconButton sx={{ p: 0 }}>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Grid>
      {loader ? (
        <Grid item xs mt={15} px={4}>
          <Loader />
        </Grid>
      ) : organizationalUnits?.length > 0 ? (
        <>
          <Grid item xs={6} sx={{ my: 2, ml: 2 }}>
            <FormProvider {...methods}>
              <Box component="form" onSubmit={handleSubmit(onSearch)}>
                <SearchBar name="query" type="submit" onClear={clear} />
              </Box>
            </FormProvider>
          </Grid>
          <Grid item xs={12}>
            {paginationComponent}
            <StyledDataGrid
              autoHeight
              hideFooter
              columns={columns}
              rows={tableData ?? []}
              page={pageNo}
              pageSize={pageSize}
            />
            {tableData.length > 10 && paginationComponent}
          </Grid>
        </>
      ) : (
        <WsCard
          sx={{ mt: 4, width: '100%', boxShadow: 'none' }}
          isButton={false}
          cardType={CardVariant.WITH_LIGHT_BG}
          middleComponent={
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={3}
            >
              <EmptyTeamsAndUsersIcon />
              <WsTypography variant={WsTypoVariants.SUB_TITLE}>Its lonely here</WsTypography>
              <Grid item>
                <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                  No {title} created in the workspace
                </WsTypography>
              </Grid>
            </Grid>
          }
        />
      )}
    </Grid>
  );
};

export default ClientUnits;
